import * as React from 'react'

import { cn } from './cn'

export const List = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement> & {
    dense?: boolean
    disablePadding?: boolean
    subheader?: React.ReactNode
    component?: React.ElementType
  }
>(
  (
    {
      dense,
      disablePadding,
      subheader,
      component: Component = 'ul',
      className,
      children,
      ...props
    },
    ref,
  ) => (
    <Component
      ref={ref}
      className={cn('m-0 list-none p-0', !disablePadding && 'py-2', className)}
      {...props}
    >
      {subheader}
      {children}
    </Component>
  ),
)
List.displayName = 'List'

export const ListSubheader = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(
      'px-4 py-2 text-xs font-semibold uppercase tracking-wider text-text-secondary',
      className,
    )}
    {...props}
  />
))
ListSubheader.displayName = 'ListSubheader'

export const ListItem = React.forwardRef<
  HTMLLIElement,
  React.LiHTMLAttributes<HTMLLIElement> & {
    dense?: boolean
    disablePadding?: boolean
    disableGutters?: boolean
    secondaryAction?: React.ReactNode
    component?: React.ElementType
    button?: boolean
  }
>(
  (
    {
      dense,
      disablePadding,
      disableGutters,
      secondaryAction,
      component: Component = 'li',
      className,
      children,
      ...props
    },
    ref,
  ) => (
    <Component
      ref={ref}
      className={cn(
        'relative flex w-full items-center',
        !disablePadding && (dense ? 'px-4 py-1' : 'px-4 py-2'),
        disableGutters && 'px-0',
        className,
      )}
      {...props}
    >
      {children}
      {secondaryAction && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2">
          {secondaryAction}
        </span>
      )}
    </Component>
  ),
)
ListItem.displayName = 'ListItem'

export interface ListItemButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean
  dense?: boolean
  component?: React.ElementType
  href?: string
  to?: string
  target?: string
}

export const ListItemButton = React.forwardRef<
  HTMLButtonElement,
  ListItemButtonProps
>(({ selected, dense, component, href, className, ...props }, ref) => {
  const Component: React.ElementType = component || (href ? 'a' : 'button')
  return (
    <Component
      ref={ref}
      href={href}
      type={Component === 'button' ? 'button' : undefined}
      className={cn(
        'flex w-full grow cursor-pointer items-center text-left transition-colors',
        dense ? 'px-4 py-1' : 'px-4 py-2',
        'hover:bg-black/5 dark:hover:bg-white/10',
        selected && 'bg-primary/10',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
      {...props}
    />
  )
})
ListItemButton.displayName = 'ListItemButton'

export const ListItemIcon = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'mr-4 inline-flex min-w-0 shrink-0 items-center text-text-secondary',
      className,
    )}
    {...props}
  />
))
ListItemIcon.displayName = 'ListItemIcon'

export const ListItemAvatar = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('mr-4 inline-flex shrink-0 items-center', className)}
    {...props}
  />
))
ListItemAvatar.displayName = 'ListItemAvatar'

export const ListItemText = React.forwardRef<
  HTMLDivElement,
  Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> & {
    primary?: React.ReactNode
    secondary?: React.ReactNode
    children?: React.ReactNode
  }
>(({ primary, secondary, className, children, ...props }, ref) => (
  <div ref={ref} className={cn('min-w-0 grow py-1', className)} {...props}>
    {(primary ?? children) != null && (
      <div className="text-base text-text-primary">{primary ?? children}</div>
    )}
    {secondary != null && (
      <div className="text-sm text-text-secondary">{secondary}</div>
    )}
  </div>
))
ListItemText.displayName = 'ListItemText'
